/* ══════════════════════════════
   PLUNGE — a simple vanilla reflex JS game.
══════════════════════════════ */

import { setupNavigation, cleanupNavigation } from '../UI/navigation.js';
import { langManager } from '../lang/langManager.js';
import {
  awardMedal,
  renderTitleMedals,
  medalModalHTML,
  getNavV,
  getNavH,
  getGameSoundEnabled,
  setGameSoundEnabled
} from '../utils/utilsJeux.js';
import {
  initJeuxModal,
  openJeuxModal,
  closeJeuxModal,
  cleanupJeuxModal
} from '../utils/utilsJeux.js';
import { log } from '../utils/logging.js';

// ── PAGE ROUTING ───────────────────────────────────────────────
const pageRouteMap = {
  home: '/',
  market: '/market',
  catering: '/catering',
  about: '/about',
  games: '/games'
};

const onNavigateHome = () => {
  window.location.href = pageRouteMap.home;
};
window.addEventListener('navigate-home', onNavigateHome);

const onNavigateSection = e => {
  const target = pageRouteMap[e.detail?.section];
  if (target) window.location.href = target;
};
window.addEventListener('navigate-section', onNavigateSection);

setupNavigation();

/* ══════════════════════════════
   PLUNGE — main game logic
══════════════════════════════ */

const t = key => langManager.getText(key);

// ── BG / circle mode constants ─────────────────────────────────
const WHACK_BG_MODE = 'crop';
const WHACK_BG_CROP_CLASS = 'plunge-bg-crop';
const WHACK_HIDE_CIRCLE_CLASS = 'hide';
const WHACK_HIDE_HIT_RING_CLASS = 'hide';

const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
const speedMult = isTouchDevice ? 0.7 : 1;

function applyPlungeBgMode(mode) {
  document.body.classList.toggle(WHACK_BG_CROP_CLASS, mode === 'crop');
}
function applyPlungeCircleModes() {
  document.body.classList.toggle('plunge-hide-circle', WHACK_HIDE_CIRCLE_CLASS === 'hide');
  document.body.classList.toggle('plunge-hide-hit-ring', WHACK_HIDE_HIT_RING_CLASS === 'hide');
}

function updatePlungeChromeH() {
  const title = document.querySelector('.page-title');
  const scorebar = document.querySelector('.scorebar');
  const timerBar = document.querySelector('#plunge-timer-bar');
  const msgEl_ = document.querySelector('.msg');
  const btn = document.querySelector('#plunge-start-btn');
  const gameDiv = document.querySelector('.game-div');
  const mainEl = document.querySelector('main');
  const gridWrap = document.getElementById('plunge-grid-wrap');

  if (!gameDiv || !mainEl) return;

  const vpW = window.innerWidth;
  const vpH = window.innerHeight;
  if (msgEl_) msgEl_.style.display = vpW <= 768 ? 'none' : '';
  const msgH = msgEl_ && vpW > 768 ? msgEl_.offsetHeight : 0;

  const gameDivGap = parseFloat(getComputedStyle(gameDiv).gap) || 12;
  const mainStyle = getComputedStyle(mainEl);
  const mainPadT = parseFloat(mainStyle.paddingTop);
  const mainPadB = parseFloat(mainStyle.paddingBottom);
  const mainPadV = mainPadT + mainPadB;

  const titleH = title ? title.offsetHeight : 0;
  const scorebarH = scorebar ? scorebar.offsetHeight : 0;
  const timerBarH = timerBar ? timerBar.offsetHeight : 0;
  const btnH = btn ? btn.offsetHeight : 0;

  const items = [scorebar, timerBar, msgEl_ && vpW > 768 ? msgEl_ : null, btn].filter(Boolean);
  const itemsTotalH = scorebarH + timerBarH + msgH + btnH;

  const gapsTotalH = gameDivGap * (items.length + 1);

  const navOffset = getNavV();
  const navHoriz = getNavH();

  const mainAvailH = mainEl.clientHeight - mainPadV;
  const chromeItems = itemsTotalH + gapsTotalH;
  const availH = mainAvailH - titleH - chromeItems;

  const chromeTotal = titleH + chromeItems + mainPadV + navOffset;
  document.documentElement.style.setProperty('--plunge-chrome-h', `${chromeTotal}px`);

  const gridW = gridWrap ? Math.round(gridWrap.offsetWidth) : 0;
  const gridH = gridWrap ? Math.round(gridWrap.offsetHeight) : 0;

  if (gridWrap) {
    const maxSide = Math.min(availH, mainEl.clientWidth - 8);
    gridWrap.style.width = `${maxSide}px`;
    gridWrap.style.height = `${maxSide}px`;
  }

  // ── Log ─────────────────────────────────────────────────────
  const logEl = (name, el) => {
    if (!el) {
      log(`  ${name.padEnd(20)} NOT FOUND`);
      return;
    }
    const r = el.getBoundingClientRect();
    log(
      `  ${name.padEnd(20)} h:${Math.round(el.offsetHeight).toString().padStart(4)}px` +
        `  top:${Math.round(r.top).toString().padStart(5)}  bot:${Math.round(r.bottom).toString().padStart(5)}` +
        `  margin: ${getComputedStyle(el).marginTop} / ${getComputedStyle(el).marginBottom}`
    );
  };

  console.groupCollapsed(
    '%c[plunge resize]',
    'color:#1309a8;font-weight:bold',
    `avail h ${Math.round(availH)}  |  window ${vpW}×${vpH}`
  );
  log(`  ${'body classes'.padEnd(20)} ${document.body.className}`);
  log('  %cLAYOUT CHAIN', 'font-weight:bold');
  log(`  ${'window'.padEnd(20)} ${vpW} × ${vpH}`);
  log(`  ${'nav (h-offset)'.padEnd(20)} ${navOffset}px`);
  log(
    `  ${'main.clientH'.padEnd(20)} ${mainEl.clientHeight}px  (padT:${Math.round(mainPadT)} padB:${Math.round(mainPadB)})`
  );
  log(`  ${'mainAvailH'.padEnd(20)} ${Math.round(mainAvailH)}px`);
  log('');
  log('  %cCHROME ELEMENTS', 'font-weight:bold');
  logEl('title (.page-title)', title);
  logEl('scorebar', scorebar);
  logEl('timer-bar', timerBar);
  logEl('msg', msgEl_ && vpW > 768 ? msgEl_ : null);
  logEl('btn', btn);
  log(`  ${'main.clientW'.padEnd(20)} ${mainEl.clientWidth}px`);

  log('');
  log('  %cGAP ACCOUNTING', 'font-weight:bold');
  log(
    `  ${'game-div gap'.padEnd(20)} ${gameDivGap.toFixed(2)}px × ${items.length - 1} gaps = ${Math.round(gapsTotalH)}px`
  );
  log(`  ${'items total H'.padEnd(20)} ${Math.round(itemsTotalH)}px`);
  log(`  ${'chrome total'.padEnd(20)} ${Math.round(chromeItems)}px  (items + gaps)`);
  log(
    `  ${'availH'.padEnd(20)} ${Math.round(availH)}px  =  mainAvailH(${Math.round(mainAvailH)}) - titleH(${titleH}) - chrome(${Math.round(chromeItems)})`
  );
  log('');
  log('  %cGRID RESULT', 'font-weight:bold');
  log(
    `  ${'--plunge-chrome-h'.padEnd(20)} ${Math.round(chromeTotal)}px  →  CSS grid: ${Math.round(vpH - chromeTotal)}px`
  );
  log(`  ${'grid actual size'.padEnd(20)} ${gridW} × ${gridH}px`);
  console.groupEnd();
}
applyPlungeBgMode(WHACK_BG_MODE);
applyPlungeCircleModes();

let _plungeResizeTimer = null;
requestAnimationFrame(() => requestAnimationFrame(updatePlungeChromeH));

const onPlungeResize = () => {
  clearTimeout(_plungeResizeTimer);
  _plungeResizeTimer = setTimeout(updatePlungeChromeH, 100);
};
window.addEventListener('resize', onPlungeResize);

// ── Sound toggle ───────────────────────────────────────────────
let plungeSoundEnabled = getGameSoundEnabled();

function initSoundToggle() {
  const btn = document.getElementById('plunge-sound-toggle');
  if (!btn) return;

  function update() {
    btn.setAttribute('aria-pressed', String(plungeSoundEnabled));
    btn.classList.toggle('muted', !plungeSoundEnabled);
    btn.title = plungeSoundEnabled ? t('soundOff') : t('soundOn');
  }

  btn.addEventListener('click', () => {
    plungeSoundEnabled = !plungeSoundEnabled;
    setGameSoundEnabled(plungeSoundEnabled);
    update();
  });

  update();
}

// ── Game constants ─────────────────────────────────────────────
const PLUNGE_HEADS = [
  'NL_Chef.webp',
  'NL_Clown.webp',
  'NL_Colombo.webp',
  'NL_Elvis.webp',
  'NL_Pecheur.webp',
  'NL_Pirate.webp',
  'NL_Punk.webp',
  'NL_Santa.webp',
  'NL_Thief.webp',
  'NL_Turc.webp',
  'NL_WomCarre.webp',
  'NL_WomFlow.webp'
];
const WHACK_DURATION = 20_000;
const WHACK_HIT_RING_DURATION = 280;
const WHACK_SPLASH_DURATION = 400;
const WHACK_SPLASH_SRC = '/jeux/plunge/splash.webp';
const WHACK_HIT_SOUND_SRC = '/jeux/plunge/water_dunk.mp3';
const WHACK_HIT_SOUND_VOLUME = 0.6;
const WHACK_HIT_SOUND_RATE_JITTER = 0.2;

// ── Game state ─────────────────────────────────────────────────
let plungeScore = 0,
  plungeMiss = 0,
  plungeActive = false;
let plungeTimer = null,
  plungeGameTimer = null,
  plungeStart = null;
let plungeBestScore = 0;

const plungeHitSound = new Audio(WHACK_HIT_SOUND_SRC);
plungeHitSound.preload = 'auto';
plungeHitSound.volume = WHACK_HIT_SOUND_VOLUME;
if ('preservesPitch' in plungeHitSound) plungeHitSound.preservesPitch = false;
if ('mozPreservesPitch' in plungeHitSound) plungeHitSound.mozPreservesPitch = false;
if ('webkitPreservesPitch' in plungeHitSound) plungeHitSound.webkitPreservesPitch = false;

function playPlungeHitSound() {
  if (!plungeSoundEnabled) return;
  try {
    const rateOffset = (Math.random() * 2 - 1) * WHACK_HIT_SOUND_RATE_JITTER;
    plungeHitSound.playbackRate = 1 + rateOffset;
    plungeHitSound.currentTime = 0;
    const p = plungeHitSound.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch {
    /* ignore */
  }
}

function clearHoleFeedback(hole) {
  if (!hole) return;
  hole.classList.remove('up', 'plungeed', 'splash-hit');
  if (hole.__hitTimer) {
    clearTimeout(hole.__hitTimer);
    hole.__hitTimer = null;
  }
  if (hole.__splashTimer) {
    clearTimeout(hole.__splashTimer);
    hole.__splashTimer = null;
  }
  if (hole.__stayTimer) {
    clearTimeout(hole.__stayTimer);
    hole.__stayTimer = null;
  }
}

// ── Grid builder ───────────────────────────────────────────────
function buildPLUNGEGrid() {
  const grid = document.getElementById('plunge-grid');
  grid.innerHTML = '';
  grid.style.cursor = "url('/jeux/plunge/CursorFistBig.webp') 16 7, pointer";
  const shuffled = [...PLUNGE_HEADS].sort(() => Math.random() - 0.5);
  for (let i = 0; i < 9; i++) {
    const head = shuffled[i % PLUNGE_HEADS.length];
    const hole = document.createElement('div');
    hole.className = 'hole';
    hole.innerHTML = `
      <div class="plunge-splash">
        <img src="${WHACK_SPLASH_SRC}" alt="" aria-hidden="true"/>
      </div>
      <div class="plunge-face"><img src="/jeux/heads/${head}" alt=""/></div>`;
    hole.addEventListener('click', () => plunge(hole));
    grid.appendChild(hole);
  }
}

// ── Game flow ──────────────────────────────────────────────────
function startPLUNGE() {
  if (plungeActive) {
    plungeActive = false;
    clearTimeout(plungeTimer);
    clearTimeout(plungeGameTimer);
    document.querySelectorAll('.hole').forEach(clearHoleFeedback);
  }

  plungeScore = 0;
  plungeMiss = 0;
  plungeActive = true;

  document.getElementById('plunge-score').textContent = 0;
  document.getElementById('plunge-miss').textContent = 0;

  const msg = document.getElementById('plunge-msg');
  msg.textContent = t('plungeGo');
  msg.className = 'msg info';

  const startBtn = document.getElementById('plunge-start-btn');
  startBtn.disabled = true;
  startBtn.textContent = t('start');

  buildPLUNGEGrid();
  plungeStart = Date.now();
  updatePlungeBar();
  clearTimeout(plungeGameTimer);
  plungeGameTimer = setTimeout(endPLUNGE, WHACK_DURATION);
  popPLUNGE();
}

function updatePlungeBar() {
  if (!plungeActive) return;
  const pct = Math.max(0, 100 - ((Date.now() - plungeStart) / WHACK_DURATION) * 100);
  document.getElementById('plunge-timer-fill').style.width = pct + '%';
  requestAnimationFrame(updatePlungeBar);
}

function popPLUNGE() {
  if (!plungeActive) return;
  const holes = document.querySelectorAll('.hole');
  const avail = [...holes].filter(h => !h.classList.contains('up'));
  if (!avail.length) {
    plungeTimer = setTimeout(popPLUNGE, 250);
    return;
  }

  const hole = avail[Math.floor(Math.random() * avail.length)];
  const stayTime = 650 + Math.random() * 700 * speedMult;
  hole.classList.add('up');
  if (hole.__stayTimer) {
    clearTimeout(hole.__stayTimer);
    hole.__stayTimer = null;
  }
  hole.__stayTimer = setTimeout(() => {
    if (hole.classList.contains('up')) {
      hole.classList.remove('up');
      plungeMiss++;
      document.getElementById('plunge-miss').textContent = plungeMiss;
    }
    hole.__stayTimer = null;
  }, stayTime);
  plungeTimer = setTimeout(popPLUNGE, 500 + Math.random() * 600 * speedMult);
}

function plunge(hole) {
  if (!plungeActive || !hole.classList.contains('up')) return;

  hole.classList.remove('up');
  hole.classList.add('plungeed', 'splash-hit');
  playPlungeHitSound();
  plungeScore++;
  document.getElementById('plunge-score').textContent = plungeScore;

  if (hole.__hitTimer) clearTimeout(hole.__hitTimer);
  hole.__hitTimer = setTimeout(() => {
    hole.classList.remove('plungeed');
    hole.__hitTimer = null;
  }, WHACK_HIT_RING_DURATION);

  if (hole.__splashTimer) clearTimeout(hole.__splashTimer);
  hole.__splashTimer = setTimeout(() => {
    hole.classList.remove('splash-hit');
    hole.__splashTimer = null;
  }, WHACK_SPLASH_DURATION);
}

function endPLUNGE() {
  plungeActive = false;
  clearTimeout(plungeTimer);
  document.querySelectorAll('.hole').forEach(clearHoleFeedback);
  document.getElementById('plunge-timer-fill').style.width = '0%';

  if (plungeScore > plungeBestScore) plungeBestScore = plungeScore;

  const accuracy =
    plungeScore + plungeMiss > 0 ? Math.round((plungeScore / (plungeScore + plungeMiss)) * 100) : 0;

  const earnedMedal = plungeMiss === 0 ? 1 : plungeMiss === 1 ? 2 : plungeMiss === 2 ? 3 : null;

  const bestMedal = awardMedal('plunge', earnedMedal);
  renderTitleMedals('plunge');

  document.getElementById('jeux-modal-body').innerHTML = `
    <div class="end-title">${t('plungeEnd')}</div>
    <div class="end-stats">
      <div class="end-stat">
        <div class="end-stat-val">${plungeScore}</div>
        <div class="end-stat-lbl">${t('plungePlunged')}</div>
      </div>
      <div class="end-stat">
        <div class="end-stat-val">${plungeMiss}</div>
        <div class="end-stat-lbl">${t('plungeMissed')}</div>
      </div>
      <div class="end-stat">
        <div class="end-stat-val">${accuracy}%</div>
        <div class="end-stat-lbl">${t('plungeAccuracy')}</div>
      </div>
    </div>
    ${medalModalHTML(earnedMedal, bestMedal, t)}`;

  const msg = document.getElementById('plunge-msg');
  msg.textContent = t('plungePrompt');
  msg.className = 'msg';

  const startBtn = document.getElementById('plunge-start-btn');
  startBtn.disabled = false;
  startBtn.textContent = t('start');

  setTimeout(() => openJeuxModal(), 600);
}

// ── Init ───────────────────────────────────────────────────────
buildPLUNGEGrid();
initSoundToggle();
renderTitleMedals('plunge');
initJeuxModal();

const startBtn = document.getElementById('plunge-start-btn');
if (startBtn) {
  startBtn.textContent = t('start');
  startBtn.disabled = false;
  startBtn.addEventListener('click', () => {
    closeJeuxModal();
    startPLUNGE();
  });
}

/* ══════════════════════════════
   CLEANUP on page hide
══════════════════════════════ */
window.addEventListener(
  'pagehide',
  () => {
    plungeActive = false;
    clearTimeout(plungeTimer);
    clearTimeout(plungeGameTimer);
    document.querySelectorAll('.hole').forEach(clearHoleFeedback);

    plungeHitSound.pause();
    plungeHitSound.src = '';

    window.removeEventListener('navigate-home', onNavigateHome);
    window.removeEventListener('navigate-section', onNavigateSection);
    window.removeEventListener('resize', onPlungeResize);

    cleanupNavigation();
    cleanupJeuxModal();
  },
  { once: true }
);
